/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { ifProp } from 'styled-tools';
import { normalizeNewLine } from 'src/core/utils/strings';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import Image from 'src/components/core/image';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import Type from 'src/components/core/typography';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  description?: string | null,
  hasTechnicalInformationDownloads?: boolean,
  imageLabel?: string | null,
  imageUrl?: string | null,
  isKiosk?: boolean,
  onGotoTechnicalInformationSection: () => void,
  title?: string | null
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  padding: ${units(13)} 0 ${units(10)};
  position: relative;

  ${media.min('ms')`
    padding: ${units(20)} 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'description'
    'actions'
    'image'
    'label';
  grid-template-columns: 100%;

  ${media.min('ms')`
    grid-template-areas:
      '. title       title       .       .       .'
      '. description description .       .       .'
      '. actions     actions     actions actions .'
      '. .           image       image   image   .'
      '. .           .           label   label   .';
    grid-template-columns: 1fr 4fr 1fr 1fr 4fr 1fr;
  `}

  ${media.min('md')`
    grid-template-columns: 2fr 3fr 1fr 1fr 4fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H5).attrs({ as: 'h2' })`
  grid-area: title;
  margin-bottom: ${units(3)};
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  grid-area: description;
  margin-bottom: ${units(5)};
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  color: ${color('grey400')};
  font-weight: 400;
`;

/**
 * `ActionsWrapper` styled component.
 */

const ActionsWrapper = styled.div`
  grid-area: actions;
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div<{ hasMargin: boolean }>`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;

  > * {
    margin-bottom: ${units(2)};
    margin-right: ${units(2)};
  }

  ${ifProp('hasMargin', css`
    margin-bottom: ${units(11)};

    ${media.min('ms')`
      margin-bottom: ${units(18)};
    `}
    `, css`
     margin-bottom: 0;
  `)}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  padding-bottom: 100%;
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Type.Small).attrs({ as: 'span', size: 'xSmall' })`
  color: ${color('grey300')};
  grid-area: label;
  line-height: 18px;
  padding: ${units(1.5)} 0 0;

  ${media.min('ms')`
    justify-self: end;
    padding: ${units(3)};
  `}
`;

/**
 * `EmptySection` styled component.
 */

const EmptySection = styled.section`
  background-color: ${color('beige100')};
  height: ${units(13)};

  ${media.min('ms')`
    height: ${units(20)};
  `}
`;

/**
 * `Effect` component.
 */

const Effect = (props: Props): ReactElement => {
  const {
    description,
    hasTechnicalInformationDownloads,
    imageLabel,
    imageUrl,
    isKiosk,
    onGotoTechnicalInformationSection,
    title
  } = props;

  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const isMobile = useBreakpoint('ms', 'max');

  if (!description && !imageUrl && !title) {
    return <EmptySection />;
  }

  return (
    <Section>
      <Container hasPadding={!isKiosk}>
        <Grid>
          {title && (
            <Title>
              <ParallaxFadeInUp parallaxOptions={{ speed: 3 }}>
                {title}
              </ParallaxFadeInUp>
            </Title>
          )}

          {description && (
            <DescriptionWrapper>
              <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                <Description>
                  <RawHtml>
                    {normalizeNewLine(description)}
                  </RawHtml>
                </Description>
              </ParallaxFadeInUp>
            </DescriptionWrapper>
          )}

          {!isKiosk && (
            <ActionsWrapper>
              <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                <Actions hasMargin={!!imageUrl}>
                  <Button href={routeResolver('whereToBuy')}>
                    {translate('common:actions.whereToBuy')}
                  </Button>

                  <Button
                    href={routeResolver('contacts')}
                    variant={'outlined'}
                  >
                    {translate('common:actions.contactUs')}
                  </Button>

                  {hasTechnicalInformationDownloads && (
                    <Button
                      onClick={onGotoTechnicalInformationSection}
                      variant={'outlined'}
                    >
                      {translate('common:actions.technicInformation')}
                    </Button>
                  )}
                </Actions>
              </ParallaxFadeInUp>
            </ActionsWrapper>
          )}

          {imageUrl && (
            <ImageWrapper>
              <ParallaxFadeInUp
                parallaxOptions={{
                  percentage: 0.1,
                  speed: isMobile ? 0 : 2
                }}
              >
                <Image
                  alt={imageLabel}
                  layout={'fill'}
                  objectFit={'cover'}
                  src={imageUrl}
                />
              </ParallaxFadeInUp>
            </ImageWrapper>
          )}

          {imageLabel && (
            <StyledLabel>
              <ParallaxFadeInUp
                parallaxOptions={{ speed: 1 }}
              >
                {imageLabel}
              </ParallaxFadeInUp>
            </StyledLabel>
          )}
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `Effect` component.
 */

export default Effect;
